import { BILL_TYPE } from '@/utils/enum'
import { EINVOICE_SALE_TYPE, EINVOICE_STATUS } from '@/utils/einvoice.enum'
import { createClientValidationError } from './einvoice.action-errors'

export const EINVOICE_BASE_URL = 'v1/einvoice'
export const ALLOWED_EINVOICE_LIST_BILL_TYPES = Object.freeze([
  BILL_TYPE.RETAIL_INVOICE,
  BILL_TYPE.WHOLESALE_INVOICE,
  BILL_TYPE.SHIPPING,
])
export const DEFAULT_EINVOICE_LIST_BILL_TYPES = Object.freeze([
  BILL_TYPE.RETAIL_INVOICE,
  BILL_TYPE.SHIPPING,
])

const BILL_TYPE_SORT_ORDER = Object.freeze(
  ALLOWED_EINVOICE_LIST_BILL_TYPES.reduce((acc, billType, index) => {
    acc[billType] = index
    return acc
  }, {}),
)


function normalizeCanonicalNullableValue(value) {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'string') {
    const normalized = value.trim()
    return normalized || null
  }

  const normalized = String(value).trim()
  return normalized || null
}

function normalizeApiRoute(route) {
  if (typeof route !== 'string' || !route.trim()) {
    return null
  }

  return route
    .trim()
    .replace(/^\/+api\/+/i, '')
    .replace(/^\/+/, '')
}

export function normalizeSaleType(saleType) {
  if (saleType === EINVOICE_SALE_TYPE.RETAIL || saleType === EINVOICE_SALE_TYPE.WHOLESALE) {
    return saleType
  }

  const normalized = typeof saleType === 'string' ? saleType.trim().toUpperCase() : ''
  if (!normalized) {
    return null
  }

  if (
    normalized.includes('WHOLESALE') ||
    normalized.includes('FAST_PORTAL') ||
    normalized.includes('PORTAL') ||
    normalized.includes('B2B')
  ) {
    return EINVOICE_SALE_TYPE.WHOLESALE
  }

  if (
    normalized.includes('RETAIL') ||
    normalized.includes('FAST_POS') ||
    normalized.includes('POS')
  ) {
    return EINVOICE_SALE_TYPE.RETAIL
  }

  return null
}

export function requireCanonicalSaleType(saleType, errorMessage) {
  const normalizedSaleType = normalizeSaleType(saleType)
  if (!normalizedSaleType) {
    throw new Error(errorMessage || 'Thiếu saleType hợp lệ để xác định lane e-invoice')
  }

  return normalizedSaleType
}

export function parseBillTypeItem(value) {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value
  }

  if (typeof value !== 'string') {
    return null
  }

  const normalized = value.trim()
  if (!/^\d+$/.test(normalized)) {
    return null
  }

  const parsed = Number(normalized)
  return Number.isInteger(parsed) ? parsed : null
}

export function normalizeBillTypes(value) {
  if (value === null || value === undefined) {
    throw createClientValidationError('BILL_TYPES_REQUIRED', 'Cần chọn ít nhất một loại hóa đơn.')
  }

  if (!Array.isArray(value)) {
    throw createClientValidationError('INVALID_BILL_TYPES', 'Bộ lọc loại hóa đơn không hợp lệ.')
  }

  if (!value.length) {
    throw createClientValidationError('BILL_TYPES_REQUIRED', 'Cần chọn ít nhất một loại hóa đơn.')
  }

  const allowed = new Set(ALLOWED_EINVOICE_LIST_BILL_TYPES)
  const normalizedSet = new Set()
  const invalidValues = []

  value.forEach((item) => {
    const billType = parseBillTypeItem(item)
    if (!allowed.has(billType)) {
      invalidValues.push(item)
      return
    }

    normalizedSet.add(billType)
  })

  if (invalidValues.length) {
    throw createClientValidationError('INVALID_BILL_TYPES', 'Bộ lọc loại hóa đơn không hợp lệ.', {
      invalidValues,
    })
  }

  if (!normalizedSet.size) {
    throw createClientValidationError('BILL_TYPES_REQUIRED', 'Cần chọn ít nhất một loại hóa đơn.')
  }

  return Array.from(normalizedSet).sort(
    (left, right) => BILL_TYPE_SORT_ORDER[left] - BILL_TYPE_SORT_ORDER[right],
  )
}

export function normalizeBillTypesOrDefault(
  value,
  defaultBillTypes = DEFAULT_EINVOICE_LIST_BILL_TYPES,
) {
  if (value === null || value === undefined) {
    return normalizeBillTypes([...defaultBillTypes])
  }

  return normalizeBillTypes(value)
}

export function requireBillTypes(value) {
  return normalizeBillTypes(value)
}

export function isRetailLikeBillType(billType) {
  const normalizedBillType = parseBillTypeItem(billType)
  return (
    normalizedBillType === BILL_TYPE.RETAIL_INVOICE || normalizedBillType === BILL_TYPE.SHIPPING
  )
}

export function resolveSaleTypeByBillType(billType) {
  const normalizedBillType = parseBillTypeItem(billType)
  if (normalizedBillType === BILL_TYPE.WHOLESALE_INVOICE) {
    return EINVOICE_SALE_TYPE.WHOLESALE
  }

  if (isRetailLikeBillType(normalizedBillType)) {
    return EINVOICE_SALE_TYPE.RETAIL
  }

  return null
}

// Maps frontend numeric status to backend string status for GET /api/v1/einvoice
const STATUS_TO_API_STRING = Object.freeze({
  [EINVOICE_STATUS.DRAFT]: 'draft',
  [EINVOICE_STATUS.READY_TO_ISSUE]: 'pending_issue',
  [EINVOICE_STATUS.ISSUING]: 'pending_issue',
  [EINVOICE_STATUS.ISSUED]: 'issued',
  [EINVOICE_STATUS.TAX_PENDING]: 'pending_tax',
  [EINVOICE_STATUS.TAX_ACCEPTED]: 'tax_accepted',
  [EINVOICE_STATUS.TAX_REJECTED]: 'tax_rejected',
  [EINVOICE_STATUS.CANCELED]: 'cancelled',
  [EINVOICE_STATUS.ADJUSTED]: 'adjusted',
  [EINVOICE_STATUS.REPLACED]: 'replaced',
  [EINVOICE_STATUS.ISSUE_FAILED]: 'failed',
})

export function normalizeStatusForListQuery(status) {
  if (status === null || status === undefined) {
    return null
  }

  // If already a valid backend string, return as-is
  if (typeof status === 'string') {
    const validStrings = new Set(Object.values(STATUS_TO_API_STRING))
    if (validStrings.has(status)) {
      return status
    }
  }

  // Convert numeric frontend status to backend string
  const mapped = STATUS_TO_API_STRING[status]
  return mapped || null
}

const ALLOWED_LIST_QUERY_KEYS = new Set([
  'billTypes',
  'status',
  'keyword',
  'buyerTaxCode',
  'codeShip',
  'dateFrom',
  'dateTo',
  'invoiceDateFrom',
  'invoiceDateTo',
  'issueDateFrom',
  'issueDateTo',
  'storeIds',
  'page',
  'limit',
])

function shouldSkipQueryParamValue(value) {
  return value === undefined || value === null || value === ''
}

export function serializeEInvoiceListParams(params = {}) {
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return ''
  }

  const filteredParams = Object.keys(params)
    .filter((key) => ALLOWED_LIST_QUERY_KEYS.has(key))
    .reduce((acc, key) => {
      // Convert numeric status to backend string format
      if (key === 'status') {
        acc[key] = normalizeStatusForListQuery(params[key])
      } else {
        acc[key] = params[key]
      }
      return acc
    }, {})

  return Object.keys(filteredParams)
    .sort()
    .reduce((parts, key) => {
      const value = filteredParams[key]
      if (shouldSkipQueryParamValue(value)) {
        return parts
      }

      if (Array.isArray(value)) {
        if (!value.length) {
          throw createClientValidationError(
            'BILL_TYPES_REQUIRED',
            'Cần chọn ít nhất một loại hóa đơn.',
          )
        }

        value.forEach((item) => {
          if (shouldSkipQueryParamValue(item)) {
            return
          }

          parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(item)}`)
        })
        return parts
      }

      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      return parts
    }, [])
    .join('&')
}

export function buildLaneKey(salesInvoiceId, saleType) {
  const normalizedSalesInvoiceId = normalizeCanonicalNullableValue(salesInvoiceId)
  const normalizedSaleType = normalizeSaleType(saleType)

  if (!normalizedSalesInvoiceId || !normalizedSaleType) {
    return null
  }

  return `${normalizedSaleType}::${normalizedSalesInvoiceId}`
}

export function buildWrapperPrefix(salesInvoiceId, saleType) {
  const normalizedSalesInvoiceId = normalizeCanonicalNullableValue(salesInvoiceId)
  if (!normalizedSalesInvoiceId) {
    throw new Error('Thiếu salesInvoiceId hợp lệ để định tuyến wrapper endpoint')
  }

  const normalizedSaleType = requireCanonicalSaleType(
    saleType,
    'Thiếu saleType hợp lệ để định tuyến wrapper endpoint',
  )

  return normalizedSaleType === EINVOICE_SALE_TYPE.WHOLESALE
    ? `${EINVOICE_BASE_URL}/wholesale-invoice/${normalizedSalesInvoiceId}`
    : `${EINVOICE_BASE_URL}/bills/${normalizedSalesInvoiceId}`
}

export function buildWrapperActionEndpoint(salesInvoiceId, saleType, action) {
  return `${buildWrapperPrefix(salesInvoiceId, saleType)}/einvoice/${action}`
}

export function buildCanonicalDetailEndpoint(salesInvoiceId, saleType) {
  return `${buildWrapperPrefix(salesInvoiceId, saleType)}/einvoice`
}

export function buildBatchFacetEndpoint(saleType) {
  const normalizedSaleType = requireCanonicalSaleType(
    saleType,
    'Thiếu saleType hợp lệ để định tuyến endpoint facets',
  )

  return normalizedSaleType === EINVOICE_SALE_TYPE.WHOLESALE
    ? `${EINVOICE_BASE_URL}/wholesale-invoice/einvoice-facets`
    : `${EINVOICE_BASE_URL}/bills/einvoice-facets`
}

export function buildDraftPrefix(salesInvoiceId, saleType) {
  return `${buildWrapperPrefix(salesInvoiceId, saleType)}/einvoice-draft`
}

export function buildDeleteDraftEndpoint(salesInvoiceId, saleType) {
  return buildDraftPrefix(salesInvoiceId, saleType)
}

export function inferPdfEndpoint(id, detail) {
  const draftLikeStatuses = [
    EINVOICE_STATUS.DRAFT,
    EINVOICE_STATUS.READY_TO_ISSUE,
    EINVOICE_STATUS.ISSUE_FAILED,
  ]
  const isDraftLike = draftLikeStatuses.includes(detail?.status)

  const artifactRoute = isDraftLike
    ? normalizeApiRoute(detail?.artifacts?.previewPdf?.route)
    : normalizeApiRoute(detail?.artifacts?.issuedPdf?.route)

  if (artifactRoute) {
    return {
      method: isDraftLike ? 'post' : 'get',
      route: artifactRoute,
    }
  }

  return {
    method: isDraftLike ? 'post' : 'get',
    route: isDraftLike
      ? `${EINVOICE_BASE_URL}/drafts/${id}/preview-pdf`
      : `${EINVOICE_BASE_URL}/${id}/pdf`,
  }
}
